import {
  NOTE_PREVIEW_BODY_FONT_SIZE_PX,
  NOTE_PREVIEW_CLOCK_DRAWING_HEIGHT_PX,
  NOTE_PREVIEW_COMMA_JOINED_SECTIONS,
  NOTE_PREVIEW_HIERARCHY_SECTION_KEYS,
  NOTE_PREVIEW_PLAIN_TEXT_SECTION_KEYS,
  NOTE_PREVIEW_TEXT_COLOR,
} from "../note-preview-constant";
import { buildHierarchyGroups } from "../note-preview-hierarchy-utils";
import { arrangeByLayout } from "../note-preview-layout-utils";
import { groupSectionLines, isPharmacyTitle } from "../note-preview-line-utils";
import { parsePharmacies } from "../note-preview-pharmacy-utils";
import { NotePreviewSection } from "../note-preview-types";
import ReportBulletGroup from "./ReportBulletGroup";
import ReportGroupHeading from "./ReportGroupHeading";
import ReportHierarchyGroup from "./ReportHierarchyGroup";
import ReportPharmacyCard from "./ReportPharmacyCard";
import ReportSectionTab from "./ReportSectionTab";
import ReportTable from "./ReportTable";

export interface NotePreviewSectionBlockProps {
  section: NotePreviewSection;
}

const includes = (keys: readonly string[], key: string) => keys.includes(key);

const renderLines = (section: NotePreviewSection) => {
  const table = section.table && <ReportTable table={section.table} />;
  if (includes(NOTE_PREVIEW_PLAIN_TEXT_SECTION_KEYS, section.key))
    return [
      ...section.lines.map((line, index) => (
        <p
          key={index}
          style={{
            margin: "2px 0",
            fontSize: NOTE_PREVIEW_BODY_FONT_SIZE_PX,
            lineHeight: 1.5,
            color: NOTE_PREVIEW_TEXT_COLOR,
          }}
        >
          {line.text}
        </p>
      )),
      table,
    ];
  if (includes(NOTE_PREVIEW_HIERARCHY_SECTION_KEYS, section.key))
    return [
      ...buildHierarchyGroups(section.lines).map((group, index) => (
        <ReportHierarchyGroup key={index} group={group} />
      )),
      table,
    ];
  const pharmacyLines = section.lines.filter(
    (line) => !!line.title && isPharmacyTitle(line.title)
  );
  const keepCommas =
    includes(NOTE_PREVIEW_COMMA_JOINED_SECTIONS, section.key) ||
    includes(NOTE_PREVIEW_COMMA_JOINED_SECTIONS, section.title);
  const groups = groupSectionLines(
    section.lines.filter((line) => !pharmacyLines.includes(line)),
    { keepCommas }
  );
  const blocks = arrangeByLayout(section.key, groups) ?? [{ groups }];
  // The section's free-text note prints last, under the pharmacy cards or the code table.
  const noteGroups =
    pharmacyLines.length || section.table
      ? groups.filter((group) => group.isInline)
      : [];
  return (
    <>
      {section.leadingTable && (
        <>
          {section.leadingTableTitle && (
            <ReportGroupHeading label={section.leadingTableTitle} />
          )}
          <ReportTable table={section.leadingTable} />
        </>
      )}
      {blocks.map((block, blockIndex) => (
        <div key={blockIndex}>
          {block.heading && <ReportGroupHeading label={block.heading} />}
          {block.groups
            .filter((group) => !noteGroups.includes(group))
            .map((group, index) => (
              <ReportBulletGroup key={index} group={group} />
            ))}
        </div>
      ))}
      {pharmacyLines
        .flatMap((line) => parsePharmacies(line.text))
        .map((pharmacy, index) => (
          <ReportPharmacyCard key={index} pharmacy={pharmacy} />
        ))}
      {section.table && <ReportTable table={section.table} />}
      {noteGroups.map((group, index) => (
        <ReportBulletGroup key={index} group={group} />
      ))}
    </>
  );
};

const NotePreviewSectionBlock = ({ section }: NotePreviewSectionBlockProps) => (
  <ReportSectionTab label={section.title}>
    {renderLines(section)}
    {section.image && (
      <img
        src={section.image}
        alt={section.title}
        style={{
          display: "block",
          height: NOTE_PREVIEW_CLOCK_DRAWING_HEIGHT_PX,
          maxWidth: "100%",
          objectFit: "contain",
          marginTop: 8,
          breakInside: "avoid",
        }}
      />
    )}
  </ReportSectionTab>
);

export default NotePreviewSectionBlock;
